import { ApiResponseJuspayMeta, VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-types";
//defs
import {
    CredEligibleData,
    PaymentGroupActions,
    PaymentMethodDowntime,
    TurboAutoPayMethod,
    TurboPayOfferComms,
    TurboPaymentMethod,
    TurboPaymentSpecificActions,
    TurboPodHalfCardEvents,
} from "@payment-ui/interfaces";
import { TurboStripData, TurboStripEventAction } from "@swiggy-private/core-ui";
//deps
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
// contexts
import { TurboPaymentContext, TurboPaymentContextValue } from "./TurboPaymentContext";

import TurboPaymentDowntimeWidget from "./components/turbo-payment-downtime-widget";
import TurboPaymentMethodWidget from "./components/turbo-payment-method-widget";
import TurboPaymentPreloader from "@payment-ui/components/turbo-payment-widget/components/turbo-payment-preloader/TurboPaymentPreloader";
//components
import TurboPaymentWidgetComms from "@payment-ui/components/turbo-payment-widget/components/turbo-payment-widget-comms/TurboPaymentWidgetComms";
import TurboPaymentWidgetCommsV2 from "./components/turbo-payment-widget-comms-v2/TurboPaymentWidgetCommsV2";
import TurboPaymentWidgetShimmer from "@payment-ui/components/turbo-payment-widget/components/turbo-payment-widget-shimmer/TurboPaymentWidgetShimmer";
import TurboPaymentWidgetShimmerV2 from "@payment-ui/components/turbo-payment-widget/components/turbo-payment-widget-shimmer/v2/TurboPaymentWidgetShimmer";
import { getCommsToShow } from "@payment-ui/helpers/TurboPayment";
//helpers
import { getTurboPayCtaTextProps } from "@payment-ui/helpers/Upi";
// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { showTurboTooltip } from "@payment-ui/helpers/TurboTooltip";
import { ProceedMode, useLocalStorageBasedFrequencyCapping } from "@swiggy-private/checkout-core";
import { getTurboTooltipViewFreqCappPayload } from "./constants";

type TurboPaymentWidgetProps = {
    loading: boolean;
    onClickChangePaymentCta: VoidFunction;
    paymentMethod?: TurboPaymentMethod | null;
    billAmount: string;
    paymentAmountWithoutDiscount: string;
    getTurboPaymentSpecificActions: (groupName: string) => TurboPaymentSpecificActions;
    hideEducationComms: boolean;
    juspayMeta?: ApiResponseJuspayMeta;
    showOverlay: boolean;
    onSlideButtonDrag: TurboPaymentContextValue["onSlideButtonDrag"];
    onSlideButtonSuccess: TurboPaymentContextValue["onSlideButtonSuccess"];
    onImpressionChangeTooltipAnimation: VoidFunction;
    onClickChangeTooltipAnimation: VoidFunction;
    showDiscountedAmount: boolean;
    balanceValue?: string | number; // for wallets + swiggy-money
    credEligibleData?: CredEligibleData;
    turboPaymentOfferComms: TurboPayOfferComms;
    paymentMethodDowntime?: PaymentMethodDowntime;
    onPaymentDowntimeWidgetLoad: VoidFunction;
    autoPaymethod: TurboAutoPayMethod;
    turboPodHalfCardEvents: TurboPodHalfCardEvents;
    manageSwipe: (disable: boolean, height?: number) => void;
    increaseSliderWidth: boolean;
    eventOnImpressionTooltip?: (stripMsg?: string) => void;
    eventOnTooltipClose?: VoidFunction;
    tooltipDetails?: {
        text?: string;
        enabled?: boolean;
        key?: string;
        icon?: string;
        maxViewFrequency?: number;
    };
    isTurboV2?: boolean;
    isPopCart?: boolean;
    onClickTurboStrip?: TurboStripEventAction;
    onImpressionTurboStrip?: TurboStripEventAction;
    turboStrip?: TurboStripData;
    cartCallbackOnTurboPaymentChange?: VoidFunction;
    billAmountShownOnCart?: string;
    uniqueId?: string;
    amountWithDiscount: string;
    isBillToCompanyExist?: boolean;
    clampDiscountText?: boolean;
    onCvvPopupOpen?: VoidFunction;
    keyFactStatementUrl?: string;
    onKeyFactStatementClick?: (url: string) => void;
};

const CHANGE_CTA_TOOLTIP_TRIGGER_TIME = 5000;

const TurboPaymentWidget = (props: TurboPaymentWidgetProps): JSX.Element | null => {
    const {
        paymentMethod,
        onClickChangePaymentCta,
        paymentAmountWithoutDiscount,
        loading,
        getTurboPaymentSpecificActions,
        hideEducationComms,
        showOverlay,
        onSlideButtonDrag,
        onImpressionChangeTooltipAnimation,
        onClickChangeTooltipAnimation,
        onSlideButtonSuccess,
        balanceValue,
        credEligibleData,
        showDiscountedAmount,
        turboPaymentOfferComms,
        paymentMethodDowntime,
        onPaymentDowntimeWidgetLoad,
        autoPaymethod,
        turboPodHalfCardEvents,
        manageSwipe,
        increaseSliderWidth,
        eventOnImpressionTooltip,
        eventOnTooltipClose,
        tooltipDetails,
        isTurboV2 = false,
        isPopCart,
        onClickTurboStrip,
        onImpressionTurboStrip,
        turboStrip = {},
        cartCallbackOnTurboPaymentChange,
        amountWithDiscount,
        isBillToCompanyExist,
        clampDiscountText = false,
        onCvvPopupOpen,
        keyFactStatementUrl,
        onKeyFactStatementClick,
    } = props;

    const turboWidgetRef = useRef<HTMLDivElement>(null);

    const { shouldProceed: shouldRenderCustomTooltip } = useLocalStorageBasedFrequencyCapping(
        getTurboTooltipViewFreqCappPayload(tooltipDetails?.key, tooltipDetails?.maxViewFrequency),
    );

    const paymentMethodUi = useMemo(() => {
        if (!paymentMethod) {
            return null;
        }

        if (!isEmpty(paymentMethodDowntime) && !isEmpty(paymentMethodDowntime?.singleClickPresentationInfo)) {
            return (
                <TurboPaymentDowntimeWidget
                    methodData={autoPaymethod}
                    paymentMethodDowntime={paymentMethodDowntime}
                    paymentActions={
                        getTurboPaymentSpecificActions(
                            VALID_PAYMENT_GROUP_TYPES.COD,
                        ) as PaymentGroupActions<TurboPaymentMethod>
                    }
                    onClickChangePaymentCta={onClickChangePaymentCta}
                    showDiscountedAmount={showDiscountedAmount}
                    amountWithDiscount={amountWithDiscount}
                    amountWithoutDiscount={paymentAmountWithoutDiscount}
                    onLoad={onPaymentDowntimeWidgetLoad}
                />
            );
        }

        const groupName = paymentMethod?.groupName?.toUpperCase();

        const turboPaymentCtaMeta = getTurboPayCtaTextProps({
            amountWithDiscount,
            amountWithoutDiscount: paymentAmountWithoutDiscount,
            methodData: paymentMethod,
        });

        return (
            <TurboPaymentMethodWidget
                methodData={paymentMethod}
                turboPaymentCtaMeta={turboPaymentCtaMeta}
                paymentActions={getTurboPaymentSpecificActions(groupName) as PaymentGroupActions<TurboPaymentMethod>}
                onClickChangePaymentCta={onClickChangePaymentCta}
                balanceValue={balanceValue}
                credEligibleData={credEligibleData}
                showDiscountedAmount={showDiscountedAmount}
                turboPodHalfCardEvents={turboPodHalfCardEvents}
                increaseSliderWidth={increaseSliderWidth}
                isTurboV2={isTurboV2}
                isPopCart={isPopCart}
                onClickTurboStrip={onClickTurboStrip}
                onImpressionTurboStrip={onImpressionTurboStrip}
                turboStrip={turboStrip}
                isBillToCompanyExist={isBillToCompanyExist}
                clampDiscountText={clampDiscountText}
                onCvvPopupOpen={onCvvPopupOpen}
                keyFactStatementUrl={keyFactStatementUrl}
                onKeyFactStatementClick={onKeyFactStatementClick}
            />
        );
    }, [
        paymentMethod,
        paymentMethodDowntime,
        amountWithDiscount,
        paymentAmountWithoutDiscount,
        getTurboPaymentSpecificActions,
        onClickChangePaymentCta,
        balanceValue,
        credEligibleData,
        showDiscountedAmount,
        turboPodHalfCardEvents,
        increaseSliderWidth,
        isTurboV2,
        isPopCart,
        onClickTurboStrip,
        onImpressionTurboStrip,
        turboStrip,
        isBillToCompanyExist,
        clampDiscountText,
        autoPaymethod,
        onPaymentDowntimeWidgetLoad,
        onCvvPopupOpen,
        keyFactStatementUrl,
        onKeyFactStatementClick,
    ]);

    const [showChangeAnimation, setShowChangeAnimation] = useState(false);

    const primaryToolipDismissCallback = useCallback(() => {
        // Change CTA tooltip should be triggered exactly 5s after the primary slider tooltip was triggered
        setTimeout(() => {
            setShowChangeAnimation(true);
        }, CHANGE_CTA_TOOLTIP_TRIGGER_TIME);
    }, []);

    const commsToShow = useMemo(() => {
        return loading ? null : getCommsToShow(turboPaymentOfferComms?.showTooltip);
    }, [turboPaymentOfferComms?.showTooltip, loading]);

    // Excluded hideEducationComms in deps array
    // because tooltip should not be visible for session which has generic crouton
    const _showTurboTooltip = useMemo(() => {
        return (
            showTurboTooltip(commsToShow, hideEducationComms) &&
            tooltipDetails?.enabled &&
            shouldRenderCustomTooltip({ mode: ProceedMode.CHECK_AND_UPDATE })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commsToShow, tooltipDetails?.enabled, shouldRenderCustomTooltip]);

    const showTurboCommsV2 = showChangeAnimation || _showTurboTooltip;

    useEffect(() => {
        if (!turboWidgetRef.current || !increaseSliderWidth) {
            return;
        }

        // using offsetHeight to include the borders (if any)
        const height = turboWidgetRef.current?.offsetHeight;
        manageSwipe(true, height);
    }, [turboWidgetRef, manageSwipe, loading, increaseSliderWidth]);

    useEffect(() => {
        if (typeof cartCallbackOnTurboPaymentChange === "function" && !isEmpty(paymentMethodUi) && !loading) {
            cartCallbackOnTurboPaymentChange?.();
        }
    }, [cartCallbackOnTurboPaymentChange, paymentMethodUi, loading]);

    if (loading) {
        if (isTurboV2) {
            return <TurboPaymentWidgetShimmerV2 />;
        }
        return <TurboPaymentWidgetShimmer />;
    }

    return (
        <TurboPaymentContext.Provider
            value={{
                onSlideButtonDrag,
                onSlideButtonSuccess,
                showChangeAnimation,
            }}
        >
            <div ref={turboWidgetRef}>
                {showOverlay && <TurboPaymentPreloader />}

                {showTurboCommsV2 && (
                    <TurboPaymentWidgetCommsV2
                        onImpressionChangeTooltipAnimation={onImpressionChangeTooltipAnimation}
                        onClickChangeTooltipAnimation={onClickChangeTooltipAnimation}
                        isChangeModeAnimation={showChangeAnimation}
                        eventOnImpressionTooltip={eventOnImpressionTooltip}
                        eventOnTooltipClose={eventOnTooltipClose}
                        tooltipDetails={tooltipDetails}
                        showTurboTooltip={_showTurboTooltip}
                        isTurboV2={isTurboV2}
                    />
                )}
                <TurboPaymentWidgetComms
                    hideEducationComms={hideEducationComms}
                    primaryToolipDismissCallback={primaryToolipDismissCallback}
                    turboPaymentOfferComms={turboPaymentOfferComms}
                    commsToShow={commsToShow}
                    method={paymentMethod?.name}
                    isTurboV2={isTurboV2}
                />
                {paymentMethodUi}
            </div>
        </TurboPaymentContext.Provider>
    );
};

export default memo(TurboPaymentWidget);
